import * as React from "react";
import {
  IButtonStyles,
  IIconProps,
  IStyleFunctionOrObject,
  ITextFieldStyleProps,
  ITextFieldStyles,
  PrimaryButton,
  Stack,
  TextField,
} from "@fluentui/react";
import * as Messages from "../../codegen/Messages";
import { buildTestId, ComponentTestIds } from "../../helpers/testIdHelper";
import { BaseInputProps } from "./BaseInput";
import {
  FormContext,
  FormErrorsContext,
  FormGroupValidationModeContext,
  FormInputGroupLayoutContext,
  FormState,
  FormValidationMode,
  FormValidationModeContext,
  getFieldErrors,
  ValidationState,
} from "./Form";
import { buildLabelWrapperTestIds, LabelWrapper, LabelWrapperTestIds } from "./LabelWrapper";

const folderIcon: IIconProps = { iconName: "FolderHorizontal" };
const buttonDimension = 24;
const btnStyles: IButtonStyles = {
  root: {
    height: buttonDimension,
    width: buttonDimension,
    padding: 0,
    minWidth: buttonDimension,
  },
};
const inputStyle: IStyleFunctionOrObject<ITextFieldStyleProps, ITextFieldStyles> = {
  root: { width: `calc(100% - ${buttonDimension}px)` },
  fieldGroup: {
    height: buttonDimension,
    minHeight: buttonDimension,
  },
  field: { cursor: "pointer" },
};

const fileInputStyle = { marginTop: "5px", width: "100%", minWidth: `${107 + buttonDimension}px` };

export interface FileUploadTestIds extends ComponentTestIds, LabelWrapperTestIds {
  button: string;
  display: string;
}

export const buildFileUploadTestIds = (testId?: string): FileUploadTestIds => ({
  ...buildLabelWrapperTestIds(testId),
  button: buildTestId(testId, "-button"),
  display: buildTestId(testId, "-display"),
  component: buildTestId(testId),
});

export interface FileUploadProps extends BaseInputProps {
  /**
   * Label for FileUpload
   */
  label: string;
  /**
   * Callback when a file is provided or the existing input is changed
   */
  onChange?: (value: string) => void
  /**
   * Callback to validate the text input
   */;
  validator?: (fileName?: string) => string[] | undefined;
}

/**
 * In order to retrieve the file contents the consumer must use the componentRef.
 * The form values will only contain the file name.
 * Supports uploading one file at a time
 */
export const FileUpload = ({
  testId,
  fieldName,
  groupName,
  label,
  onChange,
  required,
  statusInfo,
  subField,
  tooltip,
  validator,
}: FileUploadProps): JSX.Element => {
  const [fileName, setFileName] = React.useState<string>();
  const [inputRef, setInputRef] = React.useState<HTMLInputElement | null>();
  const selectedFile = React.useRef<File | undefined>();

  const { groupName: layoutGroup, layout } = React.useContext(
    FormInputGroupLayoutContext,
  );

  const group = groupName || layoutGroup;
  const form: FormState = React.useContext(FormContext);

  if (!Object.keys(form).length) {
    throw new Error("FileUpload should be used within form");
  }

  const formErrors = React.useContext(FormErrorsContext);
  const validationMode = React.useContext(FormValidationModeContext);
  const groupValidationMode = React.useContext(FormGroupValidationModeContext);

  const fieldErrors = getFieldErrors(formErrors, fieldName, group);
  const [errorMessage, setErrorMessage] = React.useState<string[]>();

  const allErrors = [...errorMessage || [], ...fieldErrors || []];

  const fileUploadTestIds = buildFileUploadTestIds(testId);

  const fieldValidation = (): void => {
    const errorMsg = validator ? validator(form.getValue(fieldName, group)) : undefined;
    const requiredMessage = required
      ? (!form.getValue(fieldName, group) ? [Messages.validation.requiredValidation()] : undefined)
      : undefined;
    const errors = [...requiredMessage || [], ...errorMsg || []];
    form.setFieldState(
      errors && errors?.length > 0 ? ValidationState.INVALID : ValidationState.VALID,
      fieldName,
      group,
    );
    setErrorMessage(errors);
  };

  const internalOnChange = (evt: React.ChangeEvent<HTMLInputElement>): void => {
    evt.preventDefault();

    selectedFile.current = evt.target.files ? evt.target.files[0] : undefined;
    form.setValue(selectedFile.current, fieldName, group);

    const uploadedFileName: string = selectedFile.current?.name || "";
    setFileName(uploadedFileName);
    if (validator || required) {
      fieldValidation();
    }

    // Should I pass the file name, or the 'File' (Not the file contents, but the File implementation)
    onChange?.(uploadedFileName);
  };

  React.useEffect(() => {
    form.registerFieldLabel(fieldName, label);
    form.registerFieldGroup(fieldName, group);

    if (validator || required) {
      form.registerField(fieldName, group);
    }
    return function cleanup() {
      if (validator || required) {
        form.unRegisterField(fieldName, group);
      }
    };
  // Only at mount time. Others props should not have side effect beyond their initial values
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (validationMode === FormValidationMode.ON || groupValidationMode[group] === FormValidationMode.ON) {
      if (validator || required) {
        fieldValidation();
      }
    }
    // Only if validation mode changes. Others props should not have side effect beyond their initial values
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationMode, groupValidationMode]);

  return (
    <LabelWrapper
      errors={allErrors}
      fieldName={fieldName}
      layout={layout}
      label={label}
      required={required}
      statusInfo={statusInfo}
      subField={subField}
      tooltip={tooltip}
      testId={testId}
    >
      <div style={fileInputStyle}>
        <Stack
          tokens={{ childrenGap: 8 }}
          horizontal
        >
          <TextField
            data-test-id={fileUploadTestIds.display}
            onClick={() => inputRef && inputRef.click()}
            styles={inputStyle}
            placeholder={Messages.actions.selectFile()}
            value={fileName}
            readOnly
          />
          <PrimaryButton
            iconProps={folderIcon}
            onClick={() => inputRef && inputRef.click()}
            styles={btnStyles}
            data-test-id={fileUploadTestIds.button}
          />
        </Stack>
        <input
          ref={input => setInputRef(input)}
          type="file"
          id="File"
          name="File"
          style={{ display: "none" }}
          onChange={internalOnChange}
          data-test-id={fileUploadTestIds.component}
        />
      </div>
    </LabelWrapper>
  );
};
